import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Link } from "../../types";
import { EntityStatus } from "../../types";
import { resolveLinkDescription } from "../../utils/linkFields";

export interface LinkHubListViewProps {
  links: Link[];
  selectedIds: Set<string>;
  onToggleSelect: (uuid: string) => void;
  onToggleSelectAll: () => void;
  allPageSelected: boolean;
  somePageSelected: boolean;
  onEdit: (link: Link) => void;
  onDelete: (link: Link) => void;
  onToggleEnabled: (link: Link) => void;
  toggleLoadingId: string | null;
}

const LinkHubListView: React.FC<LinkHubListViewProps> = ({
  links,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allPageSelected,
  somePageSelected,
  onEdit,
  onDelete,
  onToggleEnabled,
  toggleLoadingId,
}) => {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={somePageSelected && !allPageSelected}
                checked={allPageSelected && links.length > 0}
                onChange={onToggleSelectAll}
                inputProps={{ "aria-label": "Select all on this page" }}
              />
            </TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((link) => {
            const description = resolveLinkDescription(link);
            const selected = selectedIds.has(link.uuid);
            const enabled = link.status === EntityStatus.ACTIVE;

            return (
              <TableRow
                key={link.uuid}
                hover
                selected={selected}
                sx={{ "&.Mui-selected": { bgcolor: "action.selected" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected}
                    onChange={() => onToggleSelect(link.uuid)}
                    inputProps={{
                      "aria-label": `Select ${link.name}`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {link.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {description || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={link.status}
                    size="small"
                    color={
                      link.status === EntityStatus.ACTIVE
                        ? "success"
                        : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={enabled}
                        onChange={() => onToggleEnabled(link)}
                        disabled={toggleLoadingId === link.uuid}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(link)}
                      aria-label={`Edit ${link.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(link)}
                      aria-label={`Delete ${link.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {links.length === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No links match your filters.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default LinkHubListView;
